import React, { useState, useEffect } from 'react';
import { useTheme } from '@material-ui/core/styles';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Switch, FormControlLabel, Grid, Box,
  makeStyles, CircularProgress, Typography, IconButton, Paper
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import SaveIcon from '@material-ui/icons/Save';
import FormatQuoteIcon from '@material-ui/icons/FormatQuote';

import { getQuoteById, createQuote, updateQuote } from '../../services/quoteService';

const useStyles = makeStyles((theme) => ({
  dialogRoot: {
    '& .MuiDialog-paper': {
      margin: 24,
      width: 'calc(100vw - 48px)',
      maxWidth: 'calc(100vw - 48px) !important',
      height: 'calc(100vh - 48px)',
      maxHeight: 'calc(100vh - 48px)',
      borderRadius: 20,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      background: theme.palette.type === 'dark' ? '#0f172a' : '#ffffff',
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: '0 25px 70px rgba(0,0,0,0.35)',
      '@media (max-width: 600px)': {
        margin: 8,
        width: 'calc(100vw - 16px)',
        height: 'calc(100vh - 16px)',
        maxHeight: 'calc(100vh - 16px)',
        borderRadius: 14,
      },
    },
  },
  dialogHeader: {
    padding: '16px 24px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: theme.palette.type === 'dark' ? '#111827' : '#f8fafc',
    flexShrink: 0,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  headerIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    background: 'linear-gradient(135deg, rgba(16,185,129,0.2) 0%, rgba(5,150,105,0.2) 100%)',
    color: '#10b981',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    fontWeight: 700,
    fontSize: '1.2rem',
    color: theme.palette.text.primary,
    lineHeight: 1.2,
  },
  headerSubtitle: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  closeBtn: {
    color: theme.palette.text.secondary,
    borderRadius: 10,
    padding: 8,
    '&:hover': {
      backgroundColor: 'rgba(239,68,68,0.1)',
      color: '#ef4444',
    },
  },
  dialogContent: {
    padding: '24px 28px',
    overflowY: 'auto',
    flexGrow: 1,
    background: theme.palette.type === 'dark' ? '#0b1120' : '#f8fafc',
  },
  dialogActions: {
    padding: '14px 24px',
    borderTop: `1px solid ${theme.palette.divider}`,
    background: theme.palette.type === 'dark' ? '#111827' : '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
  },
  sectionCard: {
    padding: 24,
    borderRadius: 16,
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: 20,
    boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
  },
  field: {
    marginBottom: theme.spacing(2.5),
    '& .MuiInputBase-root': {
      backgroundColor: theme.palette.type === 'dark' ? 'rgba(30,41,59,0.5)' : '#ffffff',
      borderRadius: 10,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.divider,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.text.secondary,
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#10b981',
    },
  },
  arabicField: {
    '& .MuiInputBase-root': {
      direction: 'rtl',
      fontFamily: '"Traditional Arabic", "Scheherazade New", serif',
      fontSize: '1.35rem',
      lineHeight: 1.9,
      backgroundColor: theme.palette.type === 'dark' ? 'rgba(30,41,59,0.5)' : '#ffffff',
    },
  },
  saveBtn: {
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    color: '#ffffff',
    fontWeight: 600,
    borderRadius: 10,
    padding: '8px 24px',
    boxShadow: '0 4px 14px rgba(16,185,129,0.3)',
    textTransform: 'none',
    '&:hover': {
      background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
    },
  },
  cancelBtn: {
    borderRadius: 10,
    color: theme.palette.text.secondary,
    textTransform: 'none',
    fontWeight: 500,
    padding: '8px 20px',
  },
}));

export default function QuoteFormModal({ open, onClose, quoteId = null, onSuccess }) {
  const classes = useStyles();
  const theme = useTheme();

  const isEdit = Boolean(quoteId);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const [form, setForm] = useState({
    quoteText: { en: '', bn: '', ar: '' },
    author: '',
    category: '',
    isPublished: true,
  });

  useEffect(() => {
    if (!open) return;
    setErrorMsg('');
    if (quoteId) {
      setLoading(true);
      getQuoteById(quoteId)
        .then((data) => {
          if (data) {
            setForm({
              quoteText: {
                en: data.quoteText?.en || '',
                bn: data.quoteText?.bn || '',
                ar: data.quoteText?.ar || '',
              },
              author: data.author || '',
              category: data.category || '',
              isPublished: data.isPublished !== undefined ? data.isPublished : true,
            });
          }
          setLoading(false);
        })
        .catch((err) => {
          console.error(err);
          setErrorMsg('Failed to load quote details.');
          setLoading(false);
        });
    } else {
      setForm({
        quoteText: { en: '', bn: '', ar: '' },
        author: '',
        category: '',
        isPublished: true,
      });
      setLoading(false);
    }
  }, [open, quoteId]);

  const handleTextChange = (lang) => (e) => {
    setForm((prev) => ({
      ...prev,
      quoteText: { ...prev.quoteText, [lang]: e.target.value },
    }));
  };

  const handleSave = async () => {
    if (!form.quoteText.en.trim() && !form.quoteText.bn.trim()) {
      setErrorMsg('Please enter either English or Bangla quote text.');
      return;
    }
    setSaving(true);
    setErrorMsg('');
    try {
      if (isEdit) {
        await updateQuote(quoteId, form);
      } else {
        await createQuote(form);
      }
      setSaving(false);
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error(err);
      setErrorMsg(err?.message || 'Failed to save quote.');
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      className={classes.dialogRoot}
      disableBackdropClick={saving}
      disableEscapeKeyDown={saving}
      scroll="paper"
    >
      <DialogTitle disableTypography className={classes.dialogHeader}>
        <div className={classes.headerLeft}>
          <div className={classes.headerIcon}>
            <FormatQuoteIcon />
          </div>
          <div>
            <Typography className={classes.headerTitle}>
              {isEdit ? 'উক্তি সম্পাদনা (Edit Quote)' : 'নতুন ইসলামিক উক্তি যোগ করুন (Add New Quote)'}
            </Typography>
            <Typography className={classes.headerSubtitle}>
              {isEdit ? `ID: ${quoteId} • ${form.author || 'Quote'}` : 'অনুপ্রেরণামূলক ইসলামিক বাণী ও উক্তি সংরক্ষণ করুন'}
            </Typography>
          </div>
        </div>

        <IconButton
          onClick={onClose}
          className={classes.closeBtn}
          size="small"
          disabled={saving}
          title="Close Modal"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.dialogContent} dividers>
        {loading ? (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={300}>
            <CircularProgress style={{ color: '#10b981' }} size={48} thickness={4} />
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
              উক্তির তথ্য লোড হচ্ছে...
            </Typography>
          </Box>
        ) : (
          <>
            {errorMsg && (
              <Box mb={2.5} p={1.5} borderRadius={8} bgcolor="rgba(239,68,68,0.1)" border="1px solid rgba(239,68,68,0.3)">
                <Typography variant="body2" style={{ color: '#ef4444', fontWeight: 600 }}>
                  <span role="img" aria-label="error">⚠️</span> {errorMsg}
                </Typography>
              </Box>
            )}

            <Paper elevation={0} className={classes.sectionCard}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    variant="outlined"
                    label="উক্তি (বাংলা) - Bangla Quote Text"
                    value={form.quoteText.bn}
                    onChange={handleTextChange('bn')}
                    className={classes.field}
                    placeholder="বাংলায় অনুপ্রেরণামূলক উক্তি..."
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    variant="outlined"
                    label="Quote (English Text)"
                    value={form.quoteText.en}
                    onChange={handleTextChange('en')}
                    className={classes.field}
                    placeholder="English quote text..."
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    variant="outlined"
                    label="আরবি পাঠ (Arabic Text - Optional)"
                    value={form.quoteText.ar}
                    onChange={handleTextChange('ar')}
                    className={`${classes.field} ${classes.arabicField}`}
                    dir="rtl"
                    placeholder="النَّصُّ الْعَرَبِيُّ..."
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="লেখক / বক্তা (Author / Speaker)"
                    placeholder="e.g. Imam Ghazali, Hasan al-Basri"
                    value={form.author}
                    onChange={(e) => setForm({ ...form, author: e.target.value })}
                    className={classes.field}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="ক্যাটাগরি (Category / Theme)"
                    placeholder="e.g. Patience, Wisdom, Faith"
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className={classes.field}
                  />
                </Grid>
              </Grid>
            </Paper>
          </>
        )}
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <FormControlLabel
          control={
            <Switch
              checked={form.isPublished}
              onChange={(e) => setForm({ ...form, isPublished: e.target.checked })}
              color="primary"
            />
          }
          label={
            <Typography variant="body2" style={{ fontWeight: 600, color: theme.palette.text.primary }}>
              {form.isPublished ? 'অবস্থা: প্রকাশিত (Published)' : 'অবস্থা: ড্রাফট (Draft)'}
            </Typography>
          }
        />

        <Box display="flex" alignItems="center" style={{ gap: 16 }}>
          <Button onClick={onClose} className={classes.cancelBtn} disabled={saving} style={{ marginRight: 8 }}>
            Cancel
          </Button>

          <Button
            variant="contained"
            className={classes.saveBtn}
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving || loading}
          >
            {saving ? 'সংরক্ষণ হচ্ছে...' : isEdit ? 'আপডেট করুন (Save Changes)' : 'উক্তি প্রকাশ করুন (Publish Quote)'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
